import React, { FC, useState, useCallback, useEffect } from 'react';
import {
  Option,
  Combobox,
  Button,
  ButtonColor,
  ButtonSize,
  TextInput,
  TableRow,
  Table,
  Modal,
  ModalType,
  Loader,
} from '@code/innovance-react-ui-kit';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, FieldError, Controller } from 'react-hook-form';
import {
  Title,
  Separator,
  FormRow,
  FormElement,
  ButtonFormRow,
  ButtonElement,
  ObserveTableWrapper,
} from 'src/front/components/FormStyles';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import { NewObserveSchema } from 'src/front/common/utils/validationSchemas';
import { Observation, DocumentStorage } from 'src/front/api/apiInstance';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import Datepicker from 'src/front/components/Datepicker';
import { formatAlphaNumericCyrillicSpecialCharacters } from 'src/front/common/utils/formUtils';
import { NewObserveButtonResponseData, ReferenceDataType } from 'src/front/api/generated';
import ButtonNames, {
  ButtonTranslations,
  ConfirmTranslations,
} from 'src/front/common/utils/newObserveUtils';
import usePRM from 'src/front/common/hooks/usePRM';
import displayPDF from 'src/front/common/utils/domUtils';
import { TABLE_WIDTHS } from 'src/front/constant';

export interface NewObservePageData {
  beginDate: Date | null;
  endDate: Date | null;
  loanType: Option | null;
  shopType: Option | null;
  creditStats: Option | null;
  serviceList: Option | null;
  surname: string;
}
const defaultPageValues = {
  beginDate: null,
  endDate: null,
  loanType: null,
  shopType: null,
  creditStats: null,
  serviceList: null,
  surname: '',
};
const carLoanTableHeaders = [
  'general_status_label',
  'general_components_contactNumber_text',
  'general_components_fullName_text',
  'general_currency_label',
  'general_components_loanAmount_text',
  'general_date_label',
  'general_components_applicationCreationDate_text',
  'general_components_creditAmountLimit_text',
  'general_components_minDownPayment_text',
  'general_components_rate_text',
];
const otherLoanTableHeaders = [
  'general_status_label',
  'general_date_label',
  'general_components_applicationCreationDate_text',
  'general_components_contactNumber_text',
  'general_currency_label',
  'general_components_fullName_text',
  'general_components_amount_text',
  'general_comment_label',
  'general_components_promotionName_text',
];

const NewObserve: FC = () => {
  const { t } = useTranslation();
  const resolver = useYupResolver(NewObserveSchema, { todayMaxControl: true });
  const { processDate } = useUserInfoState();

  const loanTypes = usePRM(ReferenceDataType.LOANTYPE);
  const shopTypes = usePRM(ReferenceDataType.CREDITSHOPS);
  const creditStats = usePRM(ReferenceDataType.CREDITSTATUS);

  const [buttonList, setButtonList] = useState<NewObserveButtonResponseData[]>();
  const [serviceList, setServiceList] = useState<Option[]>([]);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [modalName, setModalName] = useState<string>('');
  const [loanList, setLoanList] = useState<TableRow[]>();
  const [selectedLoan, setSelectedLoan] = useState<TableRow>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const { errors, getValues, control, reset, handleSubmit, register, setValue, watch } = useForm<
    NewObservePageData
  >({
    defaultValues: {
      ...defaultPageValues,
      beginDate: processDate || null,
      endDate: processDate || null,
    },
    resolver,
  });
  const registerFields = useCallback(() => {
    register({ name: 'loanType' });
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
    register({ name: 'shopType' });
    register({ name: 'creditStats' });
    register({ name: 'surname' });
    register({ name: 'serviceList' });
  }, [register]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const handleSelectedLoan = async (loan: TableRow | undefined): Promise<void> => {
    if (selectedLoan !== loan && loan !== undefined) {
      setIsLoading(true);
      const { surname, beginDate, creditStats: stats, endDate, loanType, shopType } = getValues();
      setSelectedLoan(loan);
      reset({
        serviceList: null,
        surname,
        beginDate,
        creditStats: stats,
        shopType,
        loanType,
        endDate,
      });
      const { data } = await Observation.getButtonsVisibility({ loanId: loan?.id || '' });
      if (data.result === 'OK') {
        setButtonList(data.buttonList);
        const list: Option[] =
          data.serviceList?.map((service) => {
            return {
              label: service.serviceName,
              value: service.serviceId,
            };
          }) || [];
        setServiceList(list);
      }

      setIsLoading(false);
    }
  };

  const handleComboChange = (
    fieldName: 'loanType' | 'shopType' | 'creditStats' | 'serviceList'
  ) => async (value: Option | null): Promise<void> => {
    if (fieldName === 'loanType') {
      setValue('shopType', null);
      setValue('creditStats', null);
      setLoanList(undefined);
    }

    setValue(fieldName, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (fieldName !== 'serviceList') {
      setButtonList(undefined);
      setServiceList([]);
    }
  };

  const loadLoanList = useCallback(
    async (pageNumber?: number, forceRefresh = false): Promise<void> => {
      setLoadingTable(true);
      setSelectedLoan(undefined);
      setButtonList(undefined);
      setServiceList([]);
      const { beginDate, endDate, shopType, loanType, surname, creditStats: status } = getValues();
      const { data } = await Observation.getLoansData({
        beginDate: formatDate(beginDate),
        endDate: formatDate(endDate),
        loanType: loanType?.value || '',
        ...(watch('loanType')?.value === '300550'
          ? { shopId: shopType?.value }
          : { loanStatus: status?.value || '' }),
        surname: surname || undefined,
        pageNumber,
        refresh: !pageNumber || forceRefresh,
      });
      if (data.result === 'OK') {
        const loanLists = data.loanList.map((loan) => ({
          id: loan.loanId,
          columns:
            watch('loanType')?.value === '300550'
              ? [
                  loan.status,
                  loan.contactNumber,
                  loan.fullName,
                  loan.currency,
                  loan.loanAmount,
                  loan.date,
                  loan.applicationCreationDate,
                  loan.creditAmountLimit || '0,00',
                  loan.minimumDownPayment,
                  loan.rateOrPromotionName,
                ]
              : [
                  loan.status,
                  loan.date,
                  loan.applicationCreationDate,
                  loan.contactNumber,
                  loan.currency,
                  loan.fullName,
                  loan.loanAmount,
                  loan.comment || '',
                  loan.rateOrPromotionName,
                ],
        }));
        setLoanList(loanLists);
        setHasNextPage(data.hasNextPage);
        setPage(pageNumber || 1);
      }
      setLoadingTable(false);
    },
    [getValues, watch]
  );
  const loadPageItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      loadLoanList(pageNumber);
    },
    [loadLoanList]
  );

  const onSubmit = async (): Promise<void> => {
    setButtonList(undefined);
    setServiceList([]);
    await loadLoanList(1, true);
  };
  const handleConfirm = async (buttonName: string): Promise<void> => {
    setConfirmModalOpen(false);
    await Observation.buttonAction({ buttonName });
    await loadLoanList();
    setSelectedLoan(undefined);
    setButtonList(undefined);
    setServiceList([]);
    setModalName('');
  };
  const actionClick = async (buttonName: string): Promise<void> => {
    let documentId;
    switch (buttonName) {
      case ButtonNames.DOWNLOAD_ADDITIONAL_SERVICE_DOCUMENT: {
        documentId = watch('serviceList')?.value;
        break;
      }
      case ButtonNames.CLOSE_AN_INACTIVE_CONTRACT:
      case ButtonNames.CLOSE_ACTIVE_AGREMEENT:
      case ButtonNames.CANCEL_CLOSING_OF_ACTIVE_CONTRACT: {
        setConfirmModalOpen(true);
        setModalName(buttonName);
        break;
      }
      default: {
        const { data } = await Observation.buttonAction({ buttonName });
        if (data.result === 'OK') {
          documentId = data.documentId;
        }
        break;
      }
    }
    if (documentId) {
      const { data: response } = await DocumentStorage.getDocument({
        documentId,
      });
      if (response.result === 'OK') {
        displayPDF(response.documentData);
      }
    }
  };
  return (
    <>
      {isLoading ? <Loader /> : null}
      <Title>
        <Trans i18nKey="newObserve_title" />
      </Title>
      <Separator />
      {isConfirmModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t(ConfirmTranslations[modalName])}
          onCancel={() => setConfirmModalOpen((state) => !state)}
          onSubmit={() => handleConfirm(modalName)}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t((errors?.loanType as FieldError)?.message as string)}
              items={loanTypes}
              label={t('general_components_loanType_label')}
              name="loanType"
              onChange={handleComboChange('loanType')}
              value={watch('loanType')}
              required
            />
          </FormElement>
          {watch('loanType')?.value === '300550' ? (
            <FormElement>
              <Combobox
                errorMessage={t((errors?.shopType as FieldError)?.message as string)}
                items={shopTypes}
                label={t('general_components_shop_label')}
                name="shopType"
                onChange={handleComboChange('shopType')}
                value={watch('shopType')}
              />
            </FormElement>
          ) : null}
          {watch('loanType')?.value && watch('loanType')?.value !== '300550' ? (
            <FormElement>
              <Combobox
                errorMessage={t((errors?.creditStats as FieldError)?.message as string)}
                items={creditStats}
                label={t('newObserve_status_label')}
                name="creditStats"
                onChange={handleComboChange('creditStats')}
                value={watch('creditStats')}
              />
            </FormElement>
          ) : null}
        </FormRow>
        {watch('loanType')?.value ? (
          <>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="beginDate"
                  render={({ onChange, value }) => (
                    <Datepicker
                      errorMessage={t(errors?.beginDate?.message as string)}
                      inputLabel={`${t('general_components_startDate_label')} *`}
                      onChange={onChange}
                      value={value as Date}
                    />
                  )}
                />
              </FormElement>
              <FormElement>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ onChange, value }) => (
                    <Datepicker
                      errorMessage={t(errors?.endDate?.message as string)}
                      inputLabel={`${t('general_components_endDate_label')} *`}
                      onChange={onChange}
                      value={value as Date}
                    />
                  )}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              {watch('loanType')?.value === '300550' ? (
                <FormElement>
                  <Controller
                    control={control}
                    name="surname"
                    render={({ onChange, value }) => (
                      <TextInput
                        errorMessage={t(errors?.surname?.message as string)}
                        formatter={formatAlphaNumericCyrillicSpecialCharacters}
                        label={t('general_surname_label')}
                        maxLength={60}
                        name="surname"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </FormElement>
              ) : (
                <FormElement />
              )}
              <Button
                color={ButtonColor.Primary}
                size={ButtonSize.Medium}
                text={t('general_components_search_button')}
                type="submit"
              />
            </FormRow>
            <Separator />
            {loanList ? (
              <ObserveTableWrapper>
                <Table
                  emptyTableText={t('general_components_notFound_errorMessage')}
                  hasNextPage={hasNextPage}
                  headers={(watch('loanType')?.value === '300550'
                    ? carLoanTableHeaders
                    : otherLoanTableHeaders
                  ).map((head) => t(head))}
                  items={loanList}
                  loading={loadingTable}
                  loadPageItems={loadPageItems}
                  onSelect={handleSelectedLoan}
                  pageNumber={page}
                  selectedItem={selectedLoan}
                  tableWidth={TABLE_WIDTHS.DESKTOP_3X}
                  widths={
                    watch('loanType')?.value === '300550'
                      ? ['15', '11', '10', '10', '6', '5', '10', '8', '10', '15']
                      : ['15', '8', '11', '11', '11', '11', '7', '10', '16']
                  }
                  clearItemOnPageChange
                  withRadio
                />
              </ObserveTableWrapper>
            ) : null}
          </>
        ) : null}
        <ButtonFormRow>
          {serviceList && serviceList.length > 0 ? (
            <ButtonElement>
              <Controller
                control={control}
                name="serviceList"
                render={({ onChange, value }) => (
                  <Combobox
                    items={serviceList}
                    label={t('newObserve_addtionalServices_label')}
                    name="serviceList"
                    onChange={(e) => {
                      onChange(e);
                      handleComboChange('serviceList');
                    }}
                    value={value}
                    required
                  />
                )}
              />
            </ButtonElement>
          ) : null}
          {serviceList && serviceList.length > 0 && watch('serviceList')?.value ? (
            <ButtonElement>
              <Button
                color={ButtonColor.Secondary}
                onClick={(e) => actionClick(ButtonNames.DOWNLOAD_ADDITIONAL_SERVICE_DOCUMENT)}
                size={ButtonSize.Large}
                text={t(ButtonTranslations[ButtonNames.DOWNLOAD_ADDITIONAL_SERVICE_DOCUMENT])}
                type="button"
              />
            </ButtonElement>
          ) : null}
          {buttonList?.map((button) => {
            return button.isVisible ? (
              <ButtonElement>
                <Button
                  color={ButtonColor.Secondary}
                  onClick={(e) => actionClick(button.buttonName)}
                  size={ButtonSize.Large}
                  text={t(ButtonTranslations[button.buttonName])}
                  type="button"
                />
              </ButtonElement>
            ) : null;
          }) || []}
        </ButtonFormRow>
      </form>
    </>
  );
};

export default NewObserve;
